import type { HostPlayground } from "playground/hal/index";
import {
  AssetRecordType,
  createShapeId,
  type Editor,
  type TLExternalContent,
  type TLImageShape,
} from "tldraw";

import { rasterizePdfPage } from "@/sketch/rasterize-pdf";
import { contentAddressedName, projectFileSrc } from "@/sketch/sketch-assets";

/**
 * A dropped PDF becomes a durable underlay: the original and the page-1
 * raster both land in project blob storage, and the image shape carries the
 * calibration facts in meta. Placement assumes the sheet was drawn at
 * 1/4" = 1'-0" (factor 48); resizing the image is the v1 calibration, and a
 * proper two-point calibrate tool is a follow-up.
 */
const DEFAULT_UNDERLAY_SCALE = 48;

type FilesContent = Extract<TLExternalContent<unknown>, { type: "files" }>;
type FilesHandler = (info: FilesContent) => void | Promise<void>;

export function registerPdfUnderlayHandler(
  editor: Editor,
  playground: HostPlayground,
): void {
  // `externalContentHandlers` is excluded from the public types but is the
  // documented way to wrap a default handler rather than replace it.
  const previous = (
    editor as unknown as {
      externalContentHandlers: Record<string, FilesHandler | undefined>;
    }
  ).externalContentHandlers["files"];

  editor.registerExternalContentHandler("files", async (info) => {
    const files = info.files ?? [];
    const pdfs = files.filter((f) => f.type === "application/pdf");
    const rest = files.filter((f) => f.type !== "application/pdf");
    if (rest.length && previous) {
      await previous({ ...info, files: rest });
    }
    for (const pdf of pdfs) {
      await placePdfUnderlay(editor, playground, pdf, info.point);
    }
  });
}

async function placePdfUnderlay(
  editor: Editor,
  playground: HostPlayground,
  pdf: File,
  point: { x: number; y: number } | undefined,
): Promise<void> {
  const raster = await rasterizePdfPage(pdf);
  const stem = pdf.name.replace(/\.pdf$/i, "");

  // Keep the source sheet alongside its raster: the raster is what renders,
  // the PDF is what a re-calibration or higher-dpi re-raster starts from.
  const pdfName = await playground.upload(
    await contentAddressedName("assets/underlays", pdf.name, pdf),
    pdf,
    "application/pdf",
  );
  const rasterName = await playground.upload(
    await contentAddressedName(
      "assets/underlays",
      `${stem}-p1.png`,
      raster.blob,
    ),
    raster.blob,
    "image/png",
  );
  const src = await projectFileSrc(playground, rasterName);

  const assetId = AssetRecordType.createId();
  editor.createAssets([
    {
      id: assetId,
      typeName: "asset",
      type: "image",
      props: {
        w: raster.width,
        h: raster.height,
        src,
        mimeType: "image/png",
        name: rasterName,
        isAnimated: false,
      },
      meta: {},
    },
  ]);

  const w = raster.pageWidthInches * DEFAULT_UNDERLAY_SCALE;
  const h = raster.pageHeightInches * DEFAULT_UNDERLAY_SCALE;
  const center = point ?? editor.getViewportPageBounds().center;
  const shapeId = createShapeId();
  editor.createShape<TLImageShape>({
    id: shapeId,
    type: "image",
    x: center.x - w / 2,
    y: center.y - h / 2,
    props: { w, h, assetId },
    meta: {
      sketch: {
        underlay: {
          source: pdfName,
          page: 1,
          paperWidthInches: raster.pageWidthInches,
          paperHeightInches: raster.pageHeightInches,
          scaleRatio: DEFAULT_UNDERLAY_SCALE,
        },
      },
    },
  });
  editor.sendToBack([shapeId]);
}
